import clsx from 'clsx'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'

import SvgEdit from '@Assets/SVG/Edit'
import { Button } from '@CoreComponents/Buttons/Button'
import { useSavedCheckoutLocalData } from '@Domains/Checkout/Hooks/useSavedCheckoutLocalData'
import { AdyenContext2 } from '@Domains/Checkout/Providers/AdyenContextProvider'
import { PaymentProviderBusContext, PaymentProviders } from '@Domains/Checkout/Steps/Payment/PaymentProviderFactory/PaymentProviderBus'
import { buildAdyenComponent } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/Hooks/useAdyen'
import {
  useAdyenCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/Hooks/useAdyenCheckoutResponse'
import {
  usePreviousStoredAdyenData,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/Hooks/usePreviousStoredAdyenData'
import { CheckoutPaymentComponentProps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './AdyenWithSelector.module.scss'

interface SelectorProps {
  bic: null
  formData: {
    clientStateDataIndicator: boolean
    paymentMethod: unknown
    riskData: unknown
  }
  isValid: boolean
  issuerName: string
  timeStamp: number
  type: string
}

enum ButtonState {
  Submit = 'Submit',
  Idle = 'Idle',
}

export const AdyenSelectorInfoComponent: FC<CheckoutPaymentComponentProps> = ({
  option,
  submitPayment,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const {
    adyenConfig,
    adyenInstance,
  } = useContext(AdyenContext2)
  const {
    busData,
    setBusData,
  } = useContext(PaymentProviderBusContext)(PaymentProviders.AdyenBankSelector)
  const paymentMethod = adyenConfig?.paymentMethodsResponse?.paymentMethods?.find(a => a.type.toLowerCase() === option.type.toLowerCase())
  const {
    getPaymentData,
    hasPreviousData,
    prevPaymentData,
    setPrevPaymentData,
  } = usePreviousStoredAdyenData<SelectorProps>(option.type)
  const ref = useRef<HTMLDivElement | null>(null)
  const { saveCheckoutLocal } = useSavedCheckoutLocalData()
  const { handleAdyenResponse } = useAdyenCheckoutResponse()
  const [adyenComponent, setAdyenComponent] = useState(null)

  useEffect(() => {
    if (adyenComponent) {
      return
    }
    const component = buildAdyenComponent(adyenInstance, option.type, ref, adyenConfig, { onSubmit: undefined })
    setAdyenComponent(component)
  }, [adyenComponent, adyenConfig, adyenInstance, option.type])

  useEffect(() => {
    if (busData === ButtonState.Submit) {
      setBusData(ButtonState.Idle)
      if (adyenComponent) {
        const anyAdyenComponent: any = adyenComponent as any
        // noinspection TypeScriptValidateTypes
        const selectedIssuer: { id, name } | undefined
          = paymentMethod?.issuers?.find(issuer => issuer.id === anyAdyenComponent.data.paymentMethod.issuer)
        const paymentData = getPaymentData(anyAdyenComponent, {
          bic: null,
          formData: {
            clientStateDataIndicator: true,
            paymentMethod: anyAdyenComponent.data.paymentMethod,
            riskData: anyAdyenComponent.data.riskData,
          },
          timeStamp: Date.now(),
          isValid: !!(anyAdyenComponent.data.paymentMethod.issuer && selectedIssuer),
          type: option.type,
          issuerName: selectedIssuer?.name,
        })
        if (paymentData && paymentData.isValid) {
          saveCheckoutLocal({ savedPaymentInputData: { ...paymentData } })
          setPrevPaymentData(paymentData)
        }
        if (paymentData?.isValid) {
          submitPayment(
            paymentData.formData,
            response => handleAdyenResponse(adyenInstance, response),
          )
        }
        if (!paymentData?.isValid && !!(adyenComponent as any)?.submit) {
          (adyenComponent as any).submit()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adyenComponent, busData, paymentMethod?.issuers, option.type, prevPaymentData, saveCheckoutLocal, setBusData, submitPayment, handleAdyenResponse])

  const optionTypeWithOutLogo = ['onlineBanking_SK']

  return (
    <div className={clsx(styles.adyenSelectorInfoWrapper, cofDesignV1 && styles.cofDesignV1)}>
      <p className={styles.adyenSelectorHeader}>{t('checkout:COF_FILL_OUT_PAYMENT_INFO')}</p>
      <div className={clsx(hasPreviousData && styles.hasOldCardData)}>
        <div ref={ref} />
      </div>
      {prevPaymentData && (
        <div className={styles.prevPaymentWrapper}>
          {!optionTypeWithOutLogo.includes(option.type) && (
            <div className={styles.cardBrandIcoWrapper}>
              <img
                alt={prevPaymentData.issuerName}
                src={`https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/${option.type}/${(prevPaymentData.formData.paymentMethod as any).issuer}.svg`}
              />
            </div>
          )}
          <p>{prevPaymentData.issuerName}</p>
          {/* TODO: use translation for aria-label */}
          <button aria-label="Edit" onClick={() => setPrevPaymentData(null)} type="button">
            <SvgEdit />
          </button>
        </div>
      )}
    </div>
  )
}

export const AdyenSelectorButton: FC<CheckoutPaymentComponentProps> = () => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const { setBusData } = useContext(PaymentProviderBusContext)(PaymentProviders.AdyenBankSelector)

  return (
    <div className={styles.adyenSelectorButtonWrapper}>
      <Button
        className={clsx(!cofDesignV1 && styles.adyenSelectorButton)}
        innerSpacing="medium"
        onClick={() => {
          setBusData(ButtonState.Submit)
        }}
      >
        {t('checkout:COF_GO_TO_SECURE_PAYMENT')}
      </Button>
    </div>
  )
}
